import { toLocalDate } from './date';
import { recordAnswer, type AnswerInput } from './leitner';
import {
  applyLessonStep,
  lessonStatus,
  type LessonRequirement,
  type LessonState,
  type LessonStep,
} from './lessons';
import {
  applyFormerIds,
  exportMigrations as defaultExportMigrations,
  migrateToCurrent,
  migrations as defaultMigrations,
} from './migrate';
import {
  validateDrafts,
  validateLessonProgress,
  validateProgress,
  validateSettings,
  type SchemaCheck,
} from './schema';
import {
  applyProblemDraft,
  emptyProblemDrafts,
  validateProblemDrafts,
  type ProblemDrafts,
} from './problem-drafts';
import { computeStreak, activeDatesOf } from './streak';
import {
  createExportFile,
  IMPORT_ERROR_MESSAGES,
  parseImport,
  type ParseImportResult,
} from './transfer';
import {
  CORRUPT_KEY_PREFIX,
  DRAFTS_LIMIT,
  emptyDrafts,
  emptyLessonProgress,
  emptyProgress,
  emptySettings,
  STORAGE_KEYS,
  type CardRecord,
  type Drafts,
  type ExportFile,
  type LessonProgress,
  type ProgressState,
  type Settings,
} from './types';

/** 注入する Storage（progress-store.md §1.1）。localStorage を直接参照しない。 */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type ValueKey = 'progress' | 'settings' | 'drafts' | 'lessons' | 'problemDrafts';
const VALUE_KEYS: readonly ValueKey[] = [
  'progress',
  'settings',
  'drafts',
  'lessons',
  'problemDrafts',
];

/** UI に一度だけ知らせること（§1.1, §6.3）。 */
export type StoreNotice =
  | { kind: 'corrupt'; key: string; savedAs: string }
  | { kind: 'save-failed' }
  | { kind: 'newer-version'; key: string }
  | { kind: 'draft-too-long'; cardId: string; max: number };

/** カードの下書き 1 件の code の上限（schema の maxLength と同じ）。超える分は保存しない。 */
export const DRAFT_CODE_MAX = 65536;
export const DRAFT_TOO_LONG_MESSAGE = '書きかけが長すぎるため保存していません（上限 65,536 文字）';

export const SAVE_FAILED_MESSAGE = 'この端末に保存できませんでした。書き出しで控えを取ってください';
export const NEWER_VERSION_MESSAGE = '新しい版で保存された進捗です';

export interface StoreSnapshot {
  progress: ProgressState;
  settings: Settings;
  drafts: Drafts;
  /** レッスンの進捗（§7）。 */
  lessons: LessonProgress;
  /** 問題の書きかけ（§9、problem-format.md §8）。 */
  problemDrafts: ProblemDrafts;
}

export interface StoreOptions {
  /** 値（progress / settings / drafts / lessons）の版の移行。 */
  migrations?: Readonly<Record<number, (v: unknown) => unknown>>;
  /** エクスポートファイルの版の移行。 */
  exportMigrations?: Readonly<Record<number, (v: unknown) => unknown>>;
  /** formerIds の移し替えに使うカード（§6.3 末尾）。 */
  cards?: readonly { id: string; formerIds?: readonly string[] }[];
}

export interface ProgressStore {
  /** 3 つのキーを読み直す。起動時と storage イベントで呼ぶ。 */
  load(): StoreNotice[];
  /** storage イベントのキーが自分のものなら読み直す（後に書いた方が勝つ）。 */
  handleStorageEvent(key: string | null): StoreNotice[];
  snapshot(): StoreSnapshot;
  streak(): number;
  recordAnswer(input: AnswerInput): {
    evaluated: boolean;
    record: CardRecord;
    notices: StoreNotice[];
  };
  saveDraft(cardId: string, code: string): StoreNotice[];
  deleteDraft(cardId: string): StoreNotice[];
  /**
   * 問題の書きかけを保存する（problem-format.md §8）。key は例題 `<problemId>`、類題 `<problemId>/<exerciseId>`
   * （problemDraftKey で作る）。code が初期コード initial（例題・類題の starter）と同じなら保存せず、項目を消す。
   * 1 秒の debounce は画面側で行う。キーの形が違う・64 KiB を超えるときは書かない（超えたら save-failed）。
   */
  saveProblemDraft(key: string, code: string, initial: string): StoreNotice[];
  /** 書きかけのコード。無ければ null（画面は初期コードを出す）。 */
  loadProblemDraft(key: string): string | null;
  /** 書きかけを消す（「ひな形に戻す」）。 */
  clearProblemDraft(key: string): StoreNotice[];
  updateSettings(patch: Partial<Omit<Settings, 'version'>>): StoreNotice[];
  /**
   * レッスンの出来事を記録する（§7）。requirement（lessonRequiredSteps の結果）を渡すと、
   * 全部済んだ時点で完了日を付ける。変化が無ければ書かない。
   */
  markLessonStep(
    lessonId: string,
    step: LessonStep,
    requirement?: LessonRequirement,
  ): { state: LessonState; notices: StoreNotice[] };
  /** レッスンの状態。requirement を渡すと原稿から消えた id を数えない。 */
  lessonState(lessonId: string, requirement?: LessonRequirement): LessonState;
  /** エクスポートファイルを作り、settings.lastExportAt を更新する（§6.1）。 */
  exportFile(): { file: ExportFile; notices: StoreNotice[] };
  /** 取り込み（§6.2）。現在の状態を backup に保存してから丸ごと置き換える。 */
  importText(text: string): ParseImportResult & { notices: StoreNotice[] };
  /** backup から同じ手順で読み込む（§6.2 手順 7）。 */
  undoImport(): (ParseImportResult & { notices: StoreNotice[] }) | null;
  hasBackup(): boolean;
}

const VALIDATORS: Record<ValueKey, (d: unknown) => SchemaCheck<unknown>> = {
  progress: validateProgress,
  settings: validateSettings,
  drafts: validateDrafts,
  lessons: validateLessonProgress,
  problemDrafts: validateProblemDrafts,
};

/** 下書きを最大 {@link DRAFTS_LIMIT} 件にする。updatedAt が古いものから消す。 */
export function trimDrafts(drafts: Drafts, limit: number = DRAFTS_LIMIT): Drafts {
  const entries = Object.entries(drafts.items);
  if (entries.length <= limit) return drafts;
  entries.sort((a, b) =>
    a[1].updatedAt < b[1].updatedAt ? 1 : a[1].updatedAt > b[1].updatedAt ? -1 : 0,
  );
  return { version: 1, items: Object.fromEntries(entries.slice(0, limit)) };
}

/**
 * 進捗ストア（progress-store.md §1〜§6）。Storage と時計は注入する。
 */
export function createStore(
  storage: StorageLike,
  now: () => Date,
  opts: StoreOptions = {},
): ProgressStore {
  const table = opts.migrations ?? defaultMigrations;
  const exportTable = opts.exportMigrations ?? defaultExportMigrations;
  let state: StoreSnapshot = {
    progress: emptyProgress(),
    settings: emptySettings(),
    drafts: emptyDrafts(),
    lessons: emptyLessonProgress(),
    problemDrafts: emptyProblemDrafts(),
  };
  /** 新しい版の値が入っているキーには書かない（§6.3）。 */
  const readOnly = new Set<ValueKey>();

  function write(key: ValueKey, value: unknown, notices: StoreNotice[]): void {
    if (readOnly.has(key)) return;
    try {
      storage.setItem(STORAGE_KEYS[key], JSON.stringify(value));
    } catch {
      if (!notices.some((n) => n.kind === 'save-failed')) notices.push({ kind: 'save-failed' });
    }
  }

  function readKey(key: ValueKey, notices: StoreNotice[]): unknown {
    const storageKey = STORAGE_KEYS[key];
    let raw: string | null;
    try {
      raw = storage.getItem(storageKey);
    } catch {
      return null;
    }
    if (raw === null) return null;
    const corrupt = (): null => {
      // 元のキー名を含め、同じミリ秒に複数の項目を退避しても上書きしない
      const savedAs = `${CORRUPT_KEY_PREFIX}${key}.${now().getTime()}`;
      try {
        storage.setItem(savedAs, raw);
      } catch {
        // 退避できなくても初期状態で続ける。
      }
      notices.push({ kind: 'corrupt', key: storageKey, savedAs });
      return null;
    };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return corrupt();
    }
    const migrated = migrateToCurrent(parsed, table);
    if (!migrated.ok) {
      if (migrated.reason === 'newer-version') {
        readOnly.add(key);
        notices.push({ kind: 'newer-version', key: storageKey });
        return null;
      }
      return corrupt();
    }
    const checked = VALIDATORS[key](migrated.value);
    if (!checked.ok) return corrupt();
    if (migrated.migrated) write(key, checked.value, notices);
    return checked.value;
  }

  function loadKey(key: ValueKey, notices: StoreNotice[]): void {
    readOnly.delete(key);
    const v = readKey(key, notices);
    if (key === 'progress') {
      const p = validateProgress(v);
      const base = p.ok ? p.value : emptyProgress();
      const renamed = applyFormerIds(base, opts.cards ?? []);
      if (renamed !== base) write('progress', renamed, notices);
      state = { ...state, progress: renamed };
    } else if (key === 'settings') {
      const s = validateSettings(v);
      state = { ...state, settings: s.ok ? s.value : emptySettings() };
    } else if (key === 'drafts') {
      const d = validateDrafts(v);
      state = { ...state, drafts: d.ok ? d.value : emptyDrafts() };
    } else if (key === 'lessons') {
      const l = validateLessonProgress(v);
      state = { ...state, lessons: l.ok ? l.value : emptyLessonProgress() };
    } else {
      const pd = validateProblemDrafts(v);
      state = { ...state, problemDrafts: pd.ok ? pd.value : emptyProblemDrafts() };
    }
  }

  function load(): StoreNotice[] {
    const notices: StoreNotice[] = [];
    loadKey('progress', notices);
    loadKey('settings', notices);
    loadKey('drafts', notices);
    loadKey('lessons', notices);
    loadKey('problemDrafts', notices);
    return notices;
  }

  function replaceAll(file: ExportFile, notices: StoreNotice[]): void {
    readOnly.clear();
    state = {
      progress: file.progress,
      settings: file.settings,
      drafts: file.drafts,
      lessons: file.lessons,
      problemDrafts: file.problemDrafts ?? emptyProblemDrafts(),
    };
    write('progress', file.progress, notices);
    write('settings', file.settings, notices);
    write('drafts', file.drafts, notices);
    write('lessons', file.lessons, notices);
    write('problemDrafts', state.problemDrafts, notices);
  }

  function importFrom(
    text: string,
    backup: boolean,
  ): ParseImportResult & { notices: StoreNotice[] } {
    const notices: StoreNotice[] = [];
    const parsed = parseImport(text, table, exportTable);
    if (!parsed.ok) return { ...parsed, notices };
    if (backup) {
      const current = createExportFile(
        state.progress,
        state.settings,
        state.drafts,
        now(),
        state.lessons,
        state.problemDrafts,
      );
      try {
        storage.setItem(STORAGE_KEYS.backup, JSON.stringify(current));
      } catch {
        // 控えが取れないまま置き換えると元の記録が戻せなくなるので、取り込みを中止する（§6.3）。
        notices.push({ kind: 'save-failed' });
        return {
          ok: false,
          kind: 'backup-failed',
          message: IMPORT_ERROR_MESSAGES['backup-failed'],
          notices,
        };
      }
    }
    replaceAll(parsed.file, notices);
    return { ...parsed, notices };
  }

  return {
    load,
    handleStorageEvent(key) {
      const notices: StoreNotice[] = [];
      if (key === null) return load();
      for (const k of VALUE_KEYS) {
        if (STORAGE_KEYS[k] === key) loadKey(k, notices);
      }
      return notices;
    },
    snapshot: () => state,
    streak: () => computeStreak(activeDatesOf(state.progress.activity), toLocalDate(now())),
    recordAnswer(input) {
      const notices: StoreNotice[] = [];
      const r = recordAnswer(state.progress, input, now());
      state = { ...state, progress: r.state };
      write('progress', r.state, notices);
      return { evaluated: r.evaluated, record: r.record, notices };
    },
    saveDraft(cardId, code) {
      const notices: StoreNotice[] = [];
      // schema の上限を超える code を書くと、読み込み時に下書き全体が壊れた扱いになるので保存前に弾く
      if (code.length > DRAFT_CODE_MAX) {
        notices.push({ kind: 'draft-too-long', cardId, max: DRAFT_CODE_MAX });
        return notices;
      }
      const drafts = trimDrafts({
        version: 1,
        items: { ...state.drafts.items, [cardId]: { code, updatedAt: now().toISOString() } },
      });
      state = { ...state, drafts };
      write('drafts', drafts, notices);
      return notices;
    },
    deleteDraft(cardId) {
      const notices: StoreNotice[] = [];
      if (!(cardId in state.drafts.items)) return notices;
      const items = { ...state.drafts.items };
      delete items[cardId];
      state = { ...state, drafts: { version: 1, items } };
      write('drafts', state.drafts, notices);
      return notices;
    },
    saveProblemDraft(key, code, initial) {
      const notices: StoreNotice[] = [];
      const next = applyProblemDraft(state.problemDrafts, key, code, initial, now());
      if (next === null) {
        notices.push({ kind: 'save-failed' });
        return notices;
      }
      if (next === state.problemDrafts) return notices;
      state = { ...state, problemDrafts: next };
      write('problemDrafts', next, notices);
      return notices;
    },
    loadProblemDraft(key) {
      return Object.prototype.hasOwnProperty.call(state.problemDrafts.items, key)
        ? (state.problemDrafts.items[key]?.code ?? null)
        : null;
    },
    clearProblemDraft(key) {
      const notices: StoreNotice[] = [];
      if (!Object.prototype.hasOwnProperty.call(state.problemDrafts.items, key)) return notices;
      const items = Object.create(null) as ProblemDrafts['items'];
      Object.assign(items, state.problemDrafts.items);
      delete items[key];
      state = { ...state, problemDrafts: { version: 1, items } };
      write('problemDrafts', state.problemDrafts, notices);
      return notices;
    },
    updateSettings(patch) {
      const notices: StoreNotice[] = [];
      const settings: Settings = { ...state.settings, ...patch, version: 1 };
      state = { ...state, settings };
      write('settings', settings, notices);
      return notices;
    },
    exportFile() {
      const notices: StoreNotice[] = [];
      const at = now();
      const settings: Settings = { ...state.settings, lastExportAt: at.toISOString() };
      state = { ...state, settings };
      write('settings', settings, notices);
      return {
        file: createExportFile(
          state.progress,
          settings,
          state.drafts,
          at,
          state.lessons,
          state.problemDrafts,
        ),
        notices,
      };
    },
    markLessonStep(lessonId, step, requirement) {
      const notices: StoreNotice[] = [];
      const next = applyLessonStep(state.lessons, lessonId, step, toLocalDate(now()), requirement);
      if (next !== state.lessons) {
        state = { ...state, lessons: next };
        write('lessons', next, notices);
      }
      return { state: lessonStatus(next, lessonId, requirement), notices };
    },
    lessonState(lessonId, requirement) {
      return lessonStatus(state.lessons, lessonId, requirement);
    },
    importText: (text) => importFrom(text, true),
    undoImport() {
      let raw: string | null;
      try {
        raw = storage.getItem(STORAGE_KEYS.backup);
      } catch {
        raw = null;
      }
      if (raw === null) return null;
      const result = importFrom(raw, false);
      // 全項目の保存に成功したときだけ控えを消す。保存に失敗したまま消すと、
      // 再読み込み後に取り込み後の状態へ戻り、もう復元できない。
      const saved = result.ok && !result.notices.some((n) => n.kind === 'save-failed');
      if (saved) {
        try {
          storage.removeItem(STORAGE_KEYS.backup);
        } catch {
          // 消せなくても状態は戻っている。
        }
      }
      return result;
    },
    hasBackup() {
      try {
        return storage.getItem(STORAGE_KEYS.backup) !== null;
      } catch {
        return false;
      }
    },
  };
}

/** メモリ上の Storage（テストと保存できない環境用）。 */
export function createMemoryStorage(
  initial: Record<string, string> = {},
): StorageLike & { dump(): Record<string, string> } {
  const map = new Map(Object.entries(initial));
  return {
    getItem: (k) => map.get(k) ?? null,
    setItem: (k, v) => {
      map.set(k, String(v));
    },
    removeItem: (k) => {
      map.delete(k);
    },
    dump: () => Object.fromEntries(map),
  };
}
